/**
 * chartDataLabels — plugin inline de Chart.js para dibujar el valor de cada dato
 * sobre la gráfica, sin agregar la dependencia chartjs-plugin-datalabels.
 *
 * Se activa por dataset: basta con poner `_label` (función que formatea el valor)
 * en el dataset. El resto de las props `_*` son opcionales.
 *
 *   datasets: [{
 *     data: [...],
 *     _label: (v) => numFmt.format(v),   // requerido para que el plugin actúe
 *     _labelPos: 'endRight',             // ver POSICIONES
 *     _labelColor: '#334155',
 *     _labelFont: '700 10px Roboto, system-ui, sans-serif',
 *     _labelSkip: (v, i) => i === 0,     // opcional: omite etiquetas puntuales
 *   }]
 *
 * POSICIONES (`_labelPos`):
 *   'above'      — encima del punto (línea).            [default]
 *   'insideTop'  — dentro de la barra vertical, arriba.
 *   'endRight'   — al final de la barra horizontal.
 *   'insideArc'  — dentro de la rebanada (dona/pie); rota el texto en rebanadas angostas.
 *
 * Respeta `meta.hidden`, así que al apagar una serie desde la leyenda las
 * etiquetas desaparecen con ella.
 *
 * Extraído de CampaignsPage/ExecutiveSummary.jsx para reutilizarlo en Tráfico Web
 * y Social Media (Analytics CLMX 2026).
 */

const DEFAULT_FONT = '700 10px Roboto, system-ui, sans-serif';

// Ancho de arco (en radianes) por debajo del cual el texto se rota para caber.
const NARROW_ARC = 0.55;
// Ancho de arco por debajo del cual ni rotado cabe: se omite la etiqueta.
const TINY_ARC = 0.16;

export const chartDataLabels = {
  id: 'sharedDataLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((ds, di) => {
      if (typeof ds._label !== 'function') return;
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      ctx.save();
      ctx.font = ds._labelFont || DEFAULT_FONT;
      meta.data.forEach((el, i) => {
        const v = ds.data[i];
        if (v == null || !Number.isFinite(v)) return;
        if (typeof ds._labelSkip === 'function' && ds._labelSkip(v, i)) return;
        // En dona/pie cada arco se puede ocultar individualmente (legend por índice).
        if (meta.data[i]?.hidden) return;
        const txt = ds._label(v, i);
        if (!txt) return;

        if (ds._labelPos === 'insideTop') {
          ctx.textAlign = 'center'; ctx.textBaseline = 'top';
          ctx.fillStyle = ds._labelColor || '#fff';
          ctx.fillText(txt, el.x, el.y + 6);
        } else if (ds._labelPos === 'endRight') {
          ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
          ctx.fillStyle = ds._labelColor || '#334155';
          ctx.fillText(txt, el.x + 6, el.y);
        } else if (ds._labelPos === 'insideArc') {
          const { startAngle, endAngle, innerRadius, outerRadius, x, y } = el.getProps(
            ['startAngle', 'endAngle', 'innerRadius', 'outerRadius', 'x', 'y'], true,
          );
          const arc = endAngle - startAngle;
          if (arc < TINY_ARC) return;
          const mid = (startAngle + endAngle) / 2;
          const r = innerRadius + (outerRadius - innerRadius) / 2;
          ctx.save();
          ctx.translate(x + Math.cos(mid) * r, y + Math.sin(mid) * r);
          // Rebanada angosta: el texto se acuesta siguiendo el radio para no desbordar.
          if (arc < NARROW_ARC) {
            let rot = mid;
            // Evita que el texto quede de cabeza en el hemisferio izquierdo.
            if (mid > Math.PI / 2 && mid < (3 * Math.PI) / 2) rot += Math.PI;
            ctx.rotate(rot);
          }
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillStyle = ds._labelColor || '#fff';
          ctx.fillText(txt, 0, 0);
          ctx.restore();
        } else {
          ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
          ctx.fillStyle = ds._labelColor || '#334155';
          ctx.fillText(txt, el.x, el.y - 8);
        }
      });
      ctx.restore();
    });
  },
};

export default chartDataLabels;
